import type { Card, Rank } from './cards.ts';
import type { GinReveal } from './games/ginrummy.ts';
import type { GoFishBookSize } from './games/gofish.ts';
import type { WarStage } from './games/war.ts';

export type { GinMeld, GinReveal } from './games/ginrummy.ts';
export type { GoFishBookSize } from './games/gofish.ts';

export type GameId = 'war' | 'gofish' | 'gin';

export const GAME_NAMES: Record<GameId, string> = {
  war: 'War',
  gofish: 'Go Fish',
  gin: 'Gin Rummy',
};

export type RoomSettings = {
  /** Go Fish only: classic books of four, or the quicker pairs-of-two variant. */
  goFishBookSize: GoFishBookSize;
};

export type RoomPhase = 'lobby' | 'playing' | 'over';

export type PublicPlayer = {
  id: string;
  name: string;
  connected: boolean;
  isHost: boolean;
  isYou: boolean;
  /** Cards held out of sight. Your own hand is sent in full separately. */
  handCount: number;
  /** Short line under the name: "3 books", "out", "watching". */
  note: string;
  /** Waiting on this player to act. */
  active: boolean;
  /** Knocked out or otherwise done. */
  done: boolean;
  /** Joined after this hand was already dealt: watching, not eliminated, and dealt in next hand. */
  spectating: boolean;
};

export type WarSeat = {
  playerId: string;
  /** The revealed card, or null when it is still face down to this viewer. */
  card: Card | null;
  played: boolean;
  /** Face-down cards buried for a war. */
  burned: number;
  isWinner: boolean;
  atWar: boolean;
};

export type WarBoard = {
  kind: 'war';
  stage: WarStage;
  round: number;
  seats: WarSeat[];
  potCount: number;
  /** Milliseconds until the server advances on its own; null when it will wait. */
  autoAdvanceMs: number | null;
  championId: string | null;
};

export type GoFishBoard = {
  kind: 'gofish';
  turnId: string;
  isYourTurn: boolean;
  bookSize: GoFishBookSize;
  poolCount: number;
  books: { playerId: string; ranks: Rank[] }[];
  /** Ranks you may ask for, drawn from your own hand. */
  askableRanks: Rank[];
  /** Players you may ask, i.e. anyone else still holding cards. */
  targetIds: string[];
  over: boolean;
  winnerIds: string[];
};

export type GinBoard = {
  kind: 'gin';
  turnId: string;
  isYourTurn: boolean;
  stage: 'draw' | 'discard' | 'over';
  stockCount: number;
  topDiscard: Card | null;
  canDrawDiscard: boolean;
  /** Card ids that, discarded from your current hand, let you knock or go gin. Empty unless it is your discard turn. */
  legalKnockCardIds: string[];
  legalGinCardIds: string[];
  wall: boolean;
  reveal: GinReveal | null;
};

export type Board = WarBoard | GoFishBoard | GinBoard;

export type LogEntry = { id: number; text: string };

export type RoomView = {
  code: string;
  youId: string;
  isHost: boolean;
  game: GameId;
  settings: RoomSettings;
  phase: RoomPhase;
  players: PublicPlayer[];
  /** Your own hand, in your own order. */
  hand: Card[];
  board: Board | null;
  /** The one-line call to action for this player right now. */
  prompt: string;
  log: LogEntry[];
  canStart: boolean;
};

export type ClientMsg =
  | { t: 'hello'; playerId: string | null; name: string; code: string | null }
  | { t: 'setName'; name: string }
  | { t: 'setGame'; game: GameId }
  | { t: 'setGoFishBookSize'; value: GoFishBookSize }
  | { t: 'start' }
  | { t: 'reorderHand'; order: string[] }
  | { t: 'playCard'; cardId: string }
  | { t: 'advance' }
  | { t: 'ask'; targetId: string; rank: Rank }
  | { t: 'ginDraw'; from: 'stock' | 'discard' }
  | { t: 'ginDiscard'; cardId: string }
  | { t: 'ginKnock'; cardId: string }
  | { t: 'backToLobby' }
  | { t: 'leave' };

export type ServerMsg =
  | { t: 'joined'; playerId: string; code: string }
  | { t: 'state'; room: RoomView }
  | { t: 'error'; message: string }
  | { t: 'kicked'; reason: string };

export const MAX_NAME_LENGTH = 14;
export const ROOM_CODE_LENGTH = 4;
